<script setup lang="ts">
import {computed, ref, watch, onUnmounted} from "vue";
import debounce from "lodash.debounce";
import {Dialog, DialogHeader, DialogScrollContent, DialogTitle} from "@/core/components/ui/dialog";
import {
    Combobox,
    ComboboxAnchor,
    ComboboxEmpty,
    ComboboxGroup,
    ComboboxInput,
    ComboboxItem,
    ComboboxList
} from "@/core/components/ui/combobox";
import {Input} from "@/core/components/ui/input";
import {Textarea} from "@/core/components/ui/textarea";
import {Label} from "@/core/components/ui/label";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {secureMessagingApi} from "@/container";
import {
    MSSanteAttachment,
    MSSantePrefill,
    MSSanteSource,
    SecureMessagingRecipient,
    SecureMessagingRecipientSection
} from "@/mssante/interfaces/SecureMessaging";
import {useToastStore} from "@/stores/toast.store";
import {useMSSanteSource} from "@/mssante/composables/useMSSanteSource";
import fileHelper from "@/core/helpers/fileHelper";
import {Check, Download, Loader2, Lock, Paperclip, Search, Send, X} from "@lucide/vue";

const {prefills, consumerKey, patientName, customerId, source} = defineProps<{
    /** The messages to propose, in proposal order: the first is the priority one. */
    prefills?: MSSantePrefill[];
    /** Enables the consumer favorite recipients suggestions. */
    consumerKey?: string;
    /** The patient display name, shown above the suggested recipients. */
    patientName?: string;
    /** The patient customer id, required to write to their Mon espace santé. */
    customerId?: number;
    /**
     * What the message is written from, to link it to a program. The program an
     * enclosing page declares wins: a message written on it is about that program.
     */
    source?: MSSanteSource;
}>();
const open = defineModel<boolean>("open", {required: true});
const emit = defineEmits<{
    (e: 'sent', recipient: string): void;
}>();

const toast = useToastStore();
const contextSource = useMSSanteSource();
/** What the message is written from: the enclosing page program, or else the given source. */
const messageSource = computed(() => contextSource.value ?? source);

const recipient = ref<SecureMessagingRecipient | null>(null);
const recipients = ref<SecureMessagingRecipient[]>([]);
const favorites = ref<SecureMessagingRecipientSection[]>([]);
/** The consumer the favorites belong to, so that they never leak to another patient. */
const favoritesConsumerKey = ref<string | undefined>();
const listOpen = ref<boolean>(false);
const searchTerm = ref<string>('');
const searching = ref<boolean>(false);
const cc = ref<string>('');
const showCc = ref<boolean>(false);
const subject = ref<string>('');
const body = ref<string>('');
const attachments = ref<MSSanteAttachment[]>([]);
const sending = ref<boolean>(false);
const fileInput = ref<HTMLInputElement>();
const index = ref<number>(0);
const sentIndexes = ref<number[]>([]);
const loadingDocuments = ref<boolean>(false);

/** The message being written. */
const current = computed((): MSSantePrefill | undefined => prefills?.[index.value]);

/** Whether the message goes to the patient Mon espace santé mailbox. */
const isPatient = computed((): boolean => current.value?.audience === 'patient');

/** Whether the message being displayed has already been sent. */
const currentSent = computed((): boolean => sentIndexes.value.includes(index.value));

/**
 * Load the form from a message, downloading its documents as attachments.
 */
const applyPrefill = async (position: number) => {
    const prefill = prefills?.[position];

    index.value = position;
    recipient.value = prefill?.recipient ?? null;
    recipients.value = prefill?.recipient ? [prefill.recipient] : [];
    searchTerm.value = '';
    cc.value = (prefill?.cc ?? []).join(', ');
    showCc.value = (prefill?.cc ?? []).length > 0;
    subject.value = prefill?.subject ?? '';
    body.value = prefill?.body ?? '';
    attachments.value = [...(prefill?.attachments ?? [])];

    // The directory is only needed to write to a professional.
    if (prefill?.audience !== 'patient' && favoritesConsumerKey.value !== consumerKey) {
        loadFavorites();
    }

    const documents = prefill?.documents ?? [];

    if (!documents.length) {
        return;
    }

    loadingDocuments.value = true;

    try {
        const blobs = await Promise.all(
            documents.map(document => secureMessagingApi().document(document.url, document.source))
        );

        // Discard the downloads when the pharmacist already switched away.
        if (index.value !== position) {
            return;
        }

        attachments.value.push(...documents.map((document, order) => ({
            name: document.name,
            blob: blobs[order],
        })));
    } catch {
        toast.showError({content: "Les pièces jointes n'ont pas pu être préparées."});
    } finally {
        loadingDocuments.value = false;
    }
};

/**
 * Reset the form from the priority message each time the dialog opens.
 */
watch(open, (isOpen) => {
    if (isOpen) {
        sentIndexes.value = [];
        applyPrefill(0);
    }
}, {immediate: true});

/**
 * Load the consumer favorite recipients, suggested before any search.
 */
const loadFavorites = async () => {
    const key = consumerKey;

    favorites.value = [];
    favoritesConsumerKey.value = key;

    if (!key) {
        return;
    }

    try {
        const loaded = await secureMessagingApi().favoriteRecipients(key);

        // Discard the suggestions when the patient changed meanwhile.
        if (favoritesConsumerKey.value !== key) {
            return;
        }

        favorites.value = loaded;
        preselectGeneralPractitioner();
    } catch {
        if (favoritesConsumerKey.value === key) {
            favoritesConsumerKey.value = undefined;
        }
    }
};

/**
 * Preselect the inferred attending doctor, only when unambiguous: nothing
 * prefilled and a single general practitioner among the suggestions.
 */
const preselectGeneralPractitioner = () => {
    if (recipient.value) {
        return;
    }

    const generalPractice = favorites.value.find(
        section => section.speciality.toLowerCase().includes('médecine générale')
    );

    if (generalPractice?.recipients.length === 1) {
        recipient.value = generalPractice.recipients[0];
        recipients.value = [generalPractice.recipients[0]];
    }
};

/**
 * The display label of a recipient.
 */
const recipientLabel = (entry: SecureMessagingRecipient | null): string => {
    if (!entry) {
        return '';
    }

    if (entry.prescriptor) {
        return `${entry.prescriptor.first_name} ${entry.prescriptor.last_name}`;
    }

    return entry.email;
};

/**
 * The organization line (establishment, postal code and city) of a recipient,
 * to disambiguate similar names.
 */
const recipientOrganization = (entry: SecureMessagingRecipient): string => {
    const organization = entry.organizations?.[0];

    if (!organization) {
        return '';
    }

    return [
        organization.name,
        [organization.address?.postal_code, organization.address?.city].filter(Boolean).join(' ')
    ].filter(Boolean).join(' — ');
};

/**
 * Search the directory. The API matches each word against the first and last
 * names, regardless of the order they were typed in.
 */
const performSearch = async (term: string) => {
    const trimmed = term.trim();

    if (trimmed.length < 2) {
        return;
    }

    searching.value = true;

    try {
        recipients.value = await secureMessagingApi().search({search: trimmed});
    } finally {
        searching.value = false;
    }
};

const debouncedSearch = debounce(performSearch, 300);

/**
 * Handle the input event on the recipient search field.
 */
const onSearchInput = (event: Event) => {
    searchTerm.value = (event.target as HTMLInputElement).value;
    debouncedSearch(searchTerm.value);
};

/**
 * Whether a string looks like an email address.
 */
const isEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

/**
 * A synthetic recipient built from the search field when the pharmacist types
 * a MSSanté address directly.
 */
const manualRecipient = computed((): SecureMessagingRecipient | null => {
    const typed = searchTerm.value.trim();

    if (!isEmail(typed)) {
        return null;
    }

    return {
        id: -1,
        prescriptor: null,
        organizations: [],
        email: typed,
        type: 'manual',
        updated_at: '',
    };
});

/**
 * Whether the consumer favorite recipients are displayed, until the
 * pharmacist starts typing.
 */
const showingFavorites = computed((): boolean => !searchTerm.value.trim() && favorites.value.length > 0);

/**
 * The recipient groups to display: the consumer favorites (grouped by
 * speciality) or the search results.
 */
const recipientGroups = computed((): { heading?: string; items: SecureMessagingRecipient[] }[] => {
    if (showingFavorites.value) {
        return favorites.value.map(section => ({
            heading: section.speciality,
            items: section.recipients,
        }));
    }

    return [{items: recipients.value}];
});

/**
 * Toggle the Cc field, clearing it when hidden so no hidden copy is sent.
 */
const toggleCc = () => {
    showCc.value = !showCc.value;

    if (!showCc.value) {
        cc.value = '';
    }
};

/**
 * The carbon copy addresses, parsed from the free text field.
 */
const ccList = computed((): string[] => {
    return cc.value.split(/[;,]/)
        .map(address => address.trim())
        .filter(Boolean);
});

/**
 * Whether every carbon copy address looks like an email.
 */
const ccValid = computed((): boolean => {
    return ccList.value.every(isEmail);
});

/**
 * Whether the message can be sent. Writing to the patient needs no recipient:
 * the HDS server resolves their mailbox from their INS.
 */
const canSend = computed((): boolean => {
    if (sending.value || loadingDocuments.value || !subject.value.trim() || !body.value.trim()) {
        return false;
    }

    if (isPatient.value) {
        return !!customerId;
    }

    return (!!recipient.value || ccList.value.length > 0) && ccValid.value;
});

/**
 * The message proposed next: the first one still unsent, otherwise any other.
 */
const nextIndex = computed((): number | undefined => {
    const others = (prefills ?? [])
        .map((prefill, position) => position)
        .filter(position => position !== index.value);

    return others.find(position => !sentIndexes.value.includes(position)) ?? others[0];
});

/**
 * How a message names its recipient, used in the switch label. The patient is
 * named rather than gendered.
 */
const audienceLabel = (position: number): string => {
    if (prefills?.[position]?.audience === 'patient') {
        return patientName ? `à ${patientName}` : 'au patient';
    }

    return 'à un professionnel de santé';
};

/**
 * The switch label: the pharmacist may skip the current message entirely, or
 * write to the next recipient once this one is sent.
 */
const switchLabel = computed((): string => {
    const target = nextIndex.value;

    if (target === undefined) {
        return '';
    }

    const verb = sentIndexes.value.includes(index.value) ? 'Envoyer aussi' : 'Envoyer plutôt';

    return `${verb} ${audienceLabel(target)}`;
});

/**
 * Add the selected files to the attachments.
 */
const onFilesSelected = (event: Event) => {
    const files = (event.target as HTMLInputElement).files;

    for (const file of Array.from(files ?? [])) {
        attachments.value.push({name: file.name, blob: file});
    }

    if (fileInput.value) {
        fileInput.value.value = '';
    }
};

/**
 * Save an attachment on the disk, so the pharmacist can check it before sending.
 */
const downloadAttachment = (attachment: MSSanteAttachment) => {
    fileHelper.downloadBlob(attachment.name, attachment.blob);
};

/**
 * Remove an attachment.
 */
const removeAttachment = (index: number) => {
    attachments.value.splice(index, 1);
};

/**
 * Send the message through the HDS server. Once sent, the dialog stays open
 * when another recipient can still be written to.
 */
const send = async () => {
    const to = recipient.value?.email ?? ccList.value[0];

    if (!isPatient.value && !to) {
        return;
    }

    if (isPatient.value && !customerId) {
        return;
    }

    sending.value = true;

    try {
        if (isPatient.value) {
            await secureMessagingApi().sendToPatient(customerId!, {
                subject: subject.value.trim(),
                body: body.value,
                attachments: attachments.value,
                consumerKey,
                source: messageSource.value
            });
        } else {
            await secureMessagingApi().send({
                to: to!,
                cc: recipient.value ? ccList.value : ccList.value.slice(1),
                subject: subject.value.trim(),
                body: body.value,
                attachments: attachments.value,
                consumerKey,
                source: messageSource.value
            });
        }

        toast.showSuccess({content: 'Le message MSSanté a bien été envoyé.'});
        sentIndexes.value.push(index.value);
        emit('sent', isPatient.value ? (patientName ?? '') : (recipientLabel(recipient.value) || to!));

        if (nextIndex.value === undefined) {
            open.value = false;
        }
    } catch (error) {
        toast.showError({content: sendError(error)});
    } finally {
        sending.value = false;
    }
};

/**
 * The message shown when a send fails. A patient without a Mon espace santé
 * mailbox is a normal case the pharmacist has to understand.
 */
const sendError = (error: unknown): string => {
    const status = (error as { response?: { status?: number } })?.response?.status;

    if (isPatient.value && status === 422) {
        return "Ce patient n'a pas de boîte Mon espace santé : son identité nationale de santé est absente.";
    }

    return "L'envoi du message MSSanté a échoué. Veuillez réessayer.";
};

/**
 * Write to the next recipient, keeping what has already been sent.
 */
const switchAudience = () => {
    if (nextIndex.value !== undefined) {
        applyPrefill(nextIndex.value);
    }
};

onUnmounted(() => {
    debouncedSearch.cancel();
});
</script>

<template>
  <Dialog v-model:open="open">
    <DialogScrollContent class="max-w-2xl">
      <DialogHeader class="px-6">
        <DialogTitle class="flex items-center gap-2">
          <img src="@/assets/images/logos/logo-ms-sante.png" class="h-6 rounded-full" alt="MSSante">
          Envoyer un message MSSanté
        </DialogTitle>
      </DialogHeader>
      <div class="flex min-w-0 flex-col gap-4 px-6 pb-6">
        <div v-if="isPatient" class="flex flex-col gap-2">
          <Label>Destinataire</Label>
          <div class="flex items-center gap-2 rounded-md border bg-muted px-3 py-2 text-sm">
            <Lock class="size-4 shrink-0 text-muted-foreground" />
            <span>Mon espace santé de {{ patientName ?? 'la patiente' }}</span>
          </div>
          <p class="text-xs text-muted-foreground">
            L'adresse est résolue à partir de l'identité nationale de santé du patient.
          </p>
        </div>
        <div v-else class="flex flex-col gap-2">
          <div class="flex items-center justify-between">
            <Label>Destinataire</Label>
            <button
              type="button"
              class="text-xs text-primary hover:underline"
              :class="{'font-semibold': showCc}"
              @click="toggleCc"
            >
              Cc
            </button>
          </div>
          <Combobox
            v-model:open="listOpen"
            ignore-filter by="id" :model-value="recipient"
            @update:model-value="(value) => recipient = value as SecureMessagingRecipient | null"
          >
            <ComboboxAnchor class="w-full cursor-text">
              <div class="relative w-full items-center">
                <ComboboxInput
                  class="pr-9 bg-white cursor-text w-full"
                  :display-value="(entry) => recipientLabel(entry as SecureMessagingRecipient | null)"
                  placeholder="Rechercher un professionnel ou saisir une adresse MSSanté"
                  @input="onSearchInput"
                  @click="listOpen = true"
                  @focus="listOpen = true"
                />
                <span class="absolute end-0 inset-y-0 flex items-center justify-center px-3">
                  <Search class="size-4 text-muted-foreground" />
                </span>
              </div>
            </ComboboxAnchor>
            <ComboboxList class="w-[var(--reka-combobox-trigger-width)] max-h-72 overflow-y-auto">
              <div
                v-if="showingFavorites"
                class="px-2 py-2 text-sm font-semibold text-muted-foreground border-b"
              >
                Derniers médecins consultés par {{ patientName ?? 'le patient' }}
              </div>
              <ComboboxEmpty>
                <div v-if="searching" class="flex items-center justify-center py-2">
                  <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2" />
                  Recherche en cours...
                </div>
                <div v-else-if="searchTerm.trim()">
                  Aucun professionnel trouvé
                </div>
                <div v-else>
                  Recherchez un professionnel par son nom ou saisissez une adresse MSSanté
                </div>
              </ComboboxEmpty>
              <ComboboxGroup v-if="manualRecipient">
                <ComboboxItem :value="manualRecipient">
                  <div class="flex items-center gap-2">
                    <Send class="size-3.5 shrink-0 text-muted-foreground" />
                    <span>Envoyer à « {{ manualRecipient.email }} »</span>
                  </div>
                </ComboboxItem>
              </ComboboxGroup>
              <ComboboxGroup
                v-for="(group, index) in recipientGroups"
                :key="group.heading ?? index"
                :heading="group.heading"
              >
                <ComboboxItem v-for="entry in group.items" :key="entry.id" :value="entry">
                  <div class="flex flex-col">
                    <span>{{ recipientLabel(entry) }}</span>
                    <span v-if="recipientOrganization(entry)" class="text-xs text-muted-foreground">
                      {{ recipientOrganization(entry) }}
                    </span>
                    <span class="text-xs text-muted-foreground">{{ entry.email }}</span>
                  </div>
                </ComboboxItem>
              </ComboboxGroup>
            </ComboboxList>
          </Combobox>
        </div>
        <div v-if="showCc" class="flex flex-col gap-2">
          <Label>Cc</Label>
          <Input v-model="cc" placeholder="Adresses MSSanté en copie, séparées par des virgules" />
          <p v-if="cc && !ccValid" class="text-xs text-red-600">
            Certaines adresses en copie sont invalides.
          </p>
        </div>
        <div class="flex flex-col gap-2">
          <Label>Objet</Label>
          <Input v-model="subject" placeholder="Objet du message" maxlength="255" />
        </div>
        <div class="flex flex-col gap-2">
          <Label>Message</Label>
          <Textarea v-model="body" :rows="8" placeholder="Votre message" />
        </div>
        <div class="flex flex-col gap-2">
          <Label>Pièces jointes</Label>
          <div v-if="attachments.length" class="flex flex-col gap-1">
            <div
              v-for="(attachment, position) in attachments"
              :key="`${attachment.name}-${position}`"
              class="flex items-center justify-between rounded-md border px-3 py-1.5 text-sm"
            >
              <button
                class="flex items-center gap-2 min-w-0 text-left hover:text-primary hover:underline"
                title="Télécharger la pièce jointe"
                @click="downloadAttachment(attachment)"
              >
                <Paperclip class="size-4 shrink-0 text-muted-foreground" />
                <span class="truncate" :title="attachment.name">{{ attachment.name }}</span>
              </button>
              <div class="flex shrink-0 items-center gap-2">
                <button
                  class="text-muted-foreground hover:text-primary"
                  title="Télécharger la pièce jointe"
                  @click="downloadAttachment(attachment)"
                >
                  <Download class="size-4" />
                </button>
                <button
                  class="text-muted-foreground hover:text-red-600"
                  title="Retirer la pièce jointe"
                  @click="removeAttachment(position)"
                >
                  <X class="size-4" />
                </button>
              </div>
            </div>
          </div>
          <div v-if="loadingDocuments" class="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 class="size-3.5 animate-spin" />
            Préparation des pièces jointes...
          </div>
          <div>
            <button
              type="button"
              class="flex items-center gap-1 text-xs text-primary hover:underline"
              @click="fileInput?.click()"
            >
              <Paperclip class="size-3.5" />
              Joindre un fichier
            </button>
            <input
              ref="fileInput"
              type="file"
              class="hidden"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              @change="onFilesSelected"
            >
          </div>
        </div>
        <div class="flex items-center justify-between gap-4">
          <p v-if="currentSent" class="flex items-center gap-1.5 text-sm text-green-700">
            <Check class="size-4 shrink-0" />
            Message envoyé {{ audienceLabel(index) }}.
          </p>
          <BaseButton primary class="ml-auto" :disabled="!canSend || currentSent" @click="send">
            <Loader2 v-if="sending" class="size-4 mr-1 animate-spin" />
            <Send v-else class="size-4 mr-1" />
            Envoyer
          </BaseButton>
        </div>
        <div v-if="switchLabel" class="flex justify-center border-t pt-4">
          <button
            type="button"
            class="text-sm text-primary hover:underline"
            :disabled="sending"
            @click="switchAudience"
          >
            {{ switchLabel }}
          </button>
        </div>
      </div>
    </DialogScrollContent>
  </Dialog>
</template>
